/**
 * Rate Limiter for AI API calls
 * Tracks usage against Gemini limits using Sliding Window + Reservation system
 */

export interface RateLimitConfig {
    rpm: number;      // Requests per minute limit
    tpm: number;      // Tokens per minute limit
    rpd: number;      // Requests per day limit
    safetyMargin: number; // Use X% of limit (default 0.8)
}

interface UsageLog {
    timestamp: number;
    tokens: number;
    requests: number;
}

export interface RateLimitCapacity {
    rpm: { used: number; limit: number; available: number };
    tpm: { used: number; limit: number; available: number };
    rpd: { used: number; limit: number; available: number };
    pending: { tokens: number; requests: number };
}

// Default config for gemini-3.1-flash-lite
export const GEMINI_FLASH_LIMITS: RateLimitConfig = {
    rpm: 1000,      // 1K requests per minute
    tpm: 1000000,   // 1M tokens per minute
    rpd: 10000,     // 10K requests per day
    safetyMargin: 0.8
};

class RateLimiter {
    private config: RateLimitConfig;
    private usageHistory: UsageLog[] = [];
    private dayStartTime: number;
    private requestsToday: number = 0;
    private pendingTokens: number = 0;
    private pendingRequests: number = 0;

    constructor(config: RateLimitConfig = GEMINI_FLASH_LIMITS) {
        this.config = config;
        this.dayStartTime = this.getStartOfDay();
    }

    private getStartOfDay(): number {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        return now.getTime();
    }

    private cleanHistory(): void {
        const now = Date.now();
        const windowStart = now - 60000; // 60 seconds ago

        // Remove logs older than 60s
        this.usageHistory = this.usageHistory.filter(log => log.timestamp > windowStart);

        // Reset daily counter if a new day
        const startOfDay = this.getStartOfDay();
        if (startOfDay > this.dayStartTime) {
            this.requestsToday = 0;
            this.dayStartTime = startOfDay;
        }
    }

    private getCurrentUsage() {
        this.cleanHistory();
        return this.usageHistory.reduce((acc, log) => ({
            tokens: acc.tokens + log.tokens,
            requests: acc.requests + log.requests
        }), { tokens: 0, requests: 0 });
    }

    /**
     * Check if we can execute a request with estimated tokens.
     * Includes pending (reserved) tokens in the calculation.
     */
    canExecute(estimatedTokens: number, requestCount: number = 1): boolean {
        const usage = this.getCurrentUsage();

        const safeRpm = this.config.rpm * this.config.safetyMargin;
        const safeTpm = this.config.tpm * this.config.safetyMargin;
        const safeRpd = this.config.rpd * this.config.safetyMargin;

        return (
            usage.requests + this.pendingRequests + requestCount <= safeRpm &&
            usage.tokens + this.pendingTokens + estimatedTokens <= safeTpm &&
            this.requestsToday + this.pendingRequests + requestCount <= safeRpd
        );
    }

    /**
     * Reserve capacity before an API call (prevents parallel calls from exceeding limits).
     * Must call release() after the call completes (success or error).
     */
    reserve(estimatedTokens: number, requests: number = 1): void {
        this.pendingTokens += estimatedTokens;
        this.pendingRequests += requests;
        console.log(`[RateLimit] Reserve ${estimatedTokens.toLocaleString()} tokens (pending: ${this.pendingTokens.toLocaleString()} tokens, ${this.pendingRequests} reqs)`);
    }

    /**
     * Release previously reserved capacity.
     * Call this after API call completes, before recordUsage().
     */
    release(estimatedTokens: number, requests: number = 1): void {
        this.pendingTokens = Math.max(0, this.pendingTokens - estimatedTokens);
        this.pendingRequests = Math.max(0, this.pendingRequests - requests);
    }

    /**
     * Record actual usage after a successful request.
     * Call this AFTER release() with actual tokens from API response.
     */
    recordUsage(requests: number, tokens: number): void {
        this.cleanHistory(); // Maintenance
        this.usageHistory.push({
            timestamp: Date.now(),
            tokens,
            requests
        });
        this.requestsToday += requests;
    }

    /**
     * Get remaining capacity (includes pending reservations)
     */
    getCapacity(): RateLimitCapacity {
        const usage = this.getCurrentUsage();

        const safeRpm = Math.floor(this.config.rpm * this.config.safetyMargin);
        const safeTpm = Math.floor(this.config.tpm * this.config.safetyMargin);
        const safeRpd = Math.floor(this.config.rpd * this.config.safetyMargin);

        const effectiveTokens = usage.tokens + this.pendingTokens;
        const effectiveRequests = usage.requests + this.pendingRequests;

        return {
            rpm: {
                used: effectiveRequests,
                limit: safeRpm,
                available: Math.max(0, safeRpm - effectiveRequests)
            },
            tpm: {
                used: effectiveTokens,
                limit: safeTpm,
                available: Math.max(0, safeTpm - effectiveTokens)
            },
            rpd: {
                used: this.requestsToday + this.pendingRequests,
                limit: safeRpd,
                available: Math.max(0, safeRpd - this.requestsToday - this.pendingRequests)
            },
            pending: {
                tokens: this.pendingTokens,
                requests: this.pendingRequests
            }
        };
    }

    /**
     * Get wait time in ms until we can execute
     */
    getWaitTime(): number {
        const usage = this.getCurrentUsage();
        const safeTpm = this.config.tpm * this.config.safetyMargin;
        const safeRpm = this.config.rpm * this.config.safetyMargin;

        const effectiveTokens = usage.tokens + this.pendingTokens;
        const effectiveRequests = usage.requests + this.pendingRequests;

        if (effectiveTokens < safeTpm && effectiveRequests < safeRpm) return 0;

        // If limits exceeded, wait for the oldest log to expire
        if (this.usageHistory.length > 0) {
            const oldestLog = this.usageHistory[0];
            const now = Date.now();
            return Math.max(0, 60000 - (now - oldestLog.timestamp));
        }

        return 1000; // Fallback
    }
}

// Singleton instance
export const rateLimiter = new RateLimiter();

export default RateLimiter;

